import React, { useState, useRef, useEffect } from 'react';
import { Search } from 'lucide-react';
import api from '../api';

/**
 * Inline product search for edit line items.
 * When user types, shows a dropdown of matching products.
 * On select, calls onSelect(product) with variant info.
 */
export default function ProductSearchInput({ value, onChange, onSelect, placeholder = 'Search by product name...', className = '' }) {
  const [query, setQuery] = useState(value || '');
  const [results, setResults] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [searching, setSearching] = useState(false);
  const ref = useRef(null);
  const timeoutRef = useRef(null);

  useEffect(() => { setQuery(value || ''); }, [value]);

  useEffect(() => {
    const handleClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setShowDropdown(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const handleChange = (e) => {
    const val = e.target.value;
    setQuery(val);
    if (onChange) onChange(val);

    clearTimeout(timeoutRef.current);
    if (val.trim().length < 2) {
      setResults([]);
      setShowDropdown(false);
      return;
    }
    timeoutRef.current = setTimeout(async () => {
      setSearching(true);
      try {
        const data = await api.searchProducts(val.trim());
        // Show ALL matches (was sliced to 10 → list felt truncated). Dropdown is scrollable + capped at ~14 rows visible.
        setResults(Array.isArray(data) ? data : []);
        setShowDropdown(true);
      } catch {
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, 250);
  };

  const handleSelect = (product) => {
    setQuery(product.name || product.variant_name || '');
    setShowDropdown(false);
    if (onSelect) onSelect(product);
  };

  return (
    <div className="relative" ref={ref}>
      <div className="relative">
        <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 text-muted-foreground pointer-events-none" />
        <input
          type="text"
          value={query}
          onChange={handleChange}
          onFocus={() => { if (results.length > 0) setShowDropdown(true); }}
          placeholder={placeholder}
          className={`w-full pl-7 pr-2 py-1.5 border border-input rounded text-sm ${className}`}
          data-testid="product-search-line"
        />
      </div>
      {showDropdown && (
        <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-card border border-border rounded-md shadow-lg max-h-[28rem] overflow-y-auto" data-testid="product-search-dropdown">
          {searching ? (
            <div className="p-2 text-xs text-muted-foreground text-center">Searching...</div>
          ) : results.length > 0 ? (
            <>
              <div className="sticky top-0 bg-muted/80 backdrop-blur px-3 py-1.5 text-xs text-muted-foreground border-b border-border z-10" data-testid="product-search-count">
                {results.length} match{results.length === 1 ? '' : 'es'} — scroll to see all, or type more to narrow
              </div>
              {results.map((p, i) => (
              <button
                key={p.id || i}
                onClick={() => handleSelect(p)}
                className="w-full text-left px-3 py-2 hover:bg-muted transition-colors border-b border-border last:border-b-0"
                data-testid={`product-result-${i}`}
              >
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium truncate">{p.name || p.variant_name}</span>
                  <span className="text-xs text-muted-foreground ml-2 flex-shrink-0">${(p.unit_price || p.cost_price || 0).toFixed(2)}</span>
                </div>
                <div className="text-xs text-muted-foreground mt-0.5">
                  SKU: {p.sku} {p.quantity_on_hand !== undefined && `| Qty: ${p.quantity_on_hand}`}
                </div>
              </button>
              ))}
              {results.length >= 500 && (
                <div className="px-3 py-2 text-xs text-amber-700 bg-amber-50 border-t border-amber-200 text-center">
                  500+ matches — type more letters to narrow down
                </div>
              )}
            </>
          ) : (
            <div className="p-2 text-xs text-muted-foreground text-center">No products found</div>
          )}
        </div>
      )}
    </div>
  );
}
